import { observer } from 'mobx-react-lite';
import dynamic from 'next/dynamic';
import { useCallback } from 'react';

import { getSimilarLoadsByMainLoadId } from 'api/search';
import { PrettyLoadPreloader } from 'components/PrettyLoadPreloader';
import { useStore } from 'hooks/useStore';
import logger from 'sentry.client.config';
import { LoadType } from 'types/cargosApp/Load';
import { IReactLoadsGridProps } from 'types/global';

type TPrettyLoadProps = Omit<IReactLoadsGridProps, 'siteFeatures' | 'culture'> & {
  encodedSearchContext?: string;
};

const Fallback = () => <div />;
const LoadsGrid = dynamic(
  () =>
    // @ts-ignore
    import('PrettyLoad/PrettyLoad').catch(error => {
      logger.error(error);
      // eslint-disable-next-line no-console
      console.log('error with PrettyLoad loading: ', error);
      return Fallback;
    }),
  {
    loading: () => <PrettyLoadPreloader />,
    ssr: false,
  },
);

const PrettyLoad = (props: TPrettyLoadProps) => {
  const { callbacks, descriptor, currentPage, loads, updateLoad, encodedSearchContext, ...searchResult } = props;

  const {
    app: {
      locale,
      loading,
      serverDate,
      bulkHideTrigger,
      selectedLoads: { plainIds: selectedLoadsIds },
    },
    siteFeatures: { isSiteFeatureEnabled },
    enablerFeaturesRepo,
    ui: { isCompactPrettyLoad, isPrintMode, setPrintMode },
    dictionaries: { data },
    boardsRepo: { boardsData },
  } = useStore();

  const fetchSimilar = useCallback(
    (mainLoadId: string, options?: { take?: number; skip?: number; excludedIds?: string[] }): Promise<LoadType[]> => {
      if (!encodedSearchContext) return Promise.resolve([]);
      return getSimilarLoadsByMainLoadId(mainLoadId, encodedSearchContext, {
        take: options?.take,
        skip: options?.skip,
        excludedCargoApplicationIds: options?.excludedIds,
      });
    },
    [encodedSearchContext],
  );

  if (loading.search) {
    return <PrettyLoadPreloader />;
  }

  const shouldSkipRenderPrettyLoad = typeof window === 'undefined' || !loads || !loads.length;
  if (shouldSkipRenderPrettyLoad) return null;

  return (
    <LoadsGrid
      {...searchResult}
      // @ts-ignore
      boards={boardsData}
      bulkHideTrigger={bulkHideTrigger}
      callbacks={callbacks}
      culture={locale}
      currentPage={currentPage}
      descriptor={descriptor}
      dictionaries={data}
      enabler={enablerFeaturesRepo}
      fetchSimilar={fetchSimilar}
      isCompactAll={isCompactPrettyLoad}
      isPrintMode={isPrintMode}
      loads={loads}
      selectedLoadsIds={selectedLoadsIds}
      serverDate={serverDate}
      setPrintMode={setPrintMode}
      siteFeatures={isSiteFeatureEnabled}
      updateLoad={updateLoad}
      userId={window.atiUser.profile?.ati_code}
    />
  );
};

const connected = observer(PrettyLoad);

export { connected as PrettyLoad };
